import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, PageBreak, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';

interface AgreementData {
  primarySellerName: string;
  secondarySellerName?: string;
  propertyAddress: string;
  balance: string;
  firstPaymentDate: string;
  dateOfBalance: string;
  date: Date;
  agreementType: "partial" | "full";
  paperType?: string;
  partialDollarAmount?: string;
  brokerOfferToSeller?: string;
  partialEndDate?: string;
}

export const generateWordDocument = async (agreementData: AgreementData) => {
  const sellerNames = agreementData.secondarySellerName 
    ? `${agreementData.primarySellerName} and ${agreementData.secondarySellerName}`
    : agreementData.primarySellerName;

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount || "0");
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  // Cover Letter Content
  const coverLetterContent = [
    // Header
    new Paragraph({
      children: [
        new TextRun({
          text: "CONTRACT FUNDING GROUP, LLC",
          bold: true,
          size: 32,
          color: "8B4513"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Professional Note Purchasing Services",
          bold: true,
          size: 24,
          color: "FF6B35"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),

    // Date
    new Paragraph({
      children: [
        new TextRun({
          text: agreementData.date ? agreementData.date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }) : "",
          size: 20
        })
      ],
      alignment: AlignmentType.RIGHT,
      spacing: { after: 300 }
    }),

    // Recipient
    new Paragraph({
      children: [
        new TextRun({
          text: agreementData.primarySellerName,
          bold: true,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Land Contract Holder",
          size: 20,
          color: "666666"
        })
      ],
      spacing: { after: 200 }
    }),

    // Subject
    new Paragraph({
      children: [
        new TextRun({
          text: "RE: Purchase Proposal",
          bold: true,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Property: ${agreementData.propertyAddress}`,
          size: 20,
          color: "666666"
        })
      ],
      spacing: { after: 300 }
    }),

    // Body
    new Paragraph({
      children: [
        new TextRun({
          text: `Dear ${agreementData.primarySellerName},`,
          size: 22
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "We are pleased to present our purchase proposal.",
          size: 22
        })
      ],
      spacing: { after: 300 }
    }),

    // Proposal Summary
    new Paragraph({
      children: [
        new TextRun({
          text: "Purchase Proposal",
          bold: true,
          size: 22,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Property: ${agreementData.propertyAddress}`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Balance: ${formatCurrency(agreementData.balance)} • Offer: ${formatCurrency(agreementData.brokerOfferToSeller || "0")}`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    ...(agreementData.agreementType === "partial" && agreementData.partialDollarAmount ? [
      new Paragraph({
        children: [
          new TextRun({
            text: `Partial: ${formatCurrency(agreementData.partialDollarAmount)}`,
            size: 20
          })
        ],
        spacing: { after: 100 }
      })
    ] : []),
    new Paragraph({
      children: [
        new TextRun({
          text: `Type: ${agreementData.agreementType === "partial" ? "Partial" : "Full"} Purchase`,
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),

    // Process
    new Paragraph({
      children: [
        new TextRun({
          text: "Process (30-45 days)",
          bold: true,
          size: 22,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Due diligence → Title/payment verification → Closing/payment",
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),

    // Required Documents
    new Paragraph({
      children: [
        new TextRun({
          text: "Required Documents",
          bold: true,
          size: 22,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Original contract • Payment history • Tax records • Insurance • Borrower contacts",
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),

    // Conclusion
    new Paragraph({
      children: [
        new TextRun({
          text: "The attached Purchase Agreement outlines terms. This 30-day offer requires signed agreement.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),

    // Signature
    new Paragraph({
      children: [
        new TextRun({
          text: "Sincerely,",
          size: 20
        })
      ],
      spacing: { after: 600 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Contract Funding Group, LLC",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),

    // Footer
    new Paragraph({
      children: [
        new TextRun({
          text: "Contract Funding Group, LLC • info@contractfundinggroup.com",
          size: 18,
          color: "666666"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 400 }
    })
  ];

  // Agreement Content
  const agreementContent = agreementData.agreementType === "partial" 
    ? generatePartialAgreementContent(agreementData, sellerNames)
    : generateFullAgreementContent(agreementData, sellerNames);

  const doc = new Document({
    sections: [
      {
        children: coverLetterContent,
      },
      {
        children: agreementContent,
      }
    ]
  });

  return await Packer.toBlob(doc);
};

const generatePartialAgreementContent = (agreementData: AgreementData, sellerNames: string) => {
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "Commitment to Purchase Agreement",
          bold: true,
          size: 24
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `This Purchase Agreement made and entered into ${agreementData.date ? agreementData.date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "________________"},`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `The property address is: ${agreementData.propertyAddress || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Purchaser agrees to buy the next $${agreementData.partialDollarAmount || "________________"} due and you agree to sell the above described sum for $${agreementData.brokerOfferToSeller || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"} and ending with the payment due ${agreementData.partialEndDate || "________________"} assuming there are no unpaid fees or costs accumulated by the borrowers.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "2. COSTS: PURCHASER will pay title and appraisal costs",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "3. SELLER warrants that all real property taxes that are due and payable have been paid.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "4. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "5. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `6. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "7. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),
    
    // Signature lines
    new Paragraph({
      children: [
        new TextRun({
          text: "______________________________________                    ______________________________________",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${agreementData.primarySellerName || "Primary Seller"}                                        Contract Funding Group, LLC`,
          size: 20
        })
      ],
      spacing: { after: 200 }
    }),
    ...(agreementData.secondarySellerName ? [
      new Paragraph({
        children: [
          new TextRun({
            text: "______________________________________",
            size: 20
          })
        ],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: agreementData.secondarySellerName,
            size: 20
          })
        ],
        spacing: { after: 200 }
      })
    ] : [])
  ];
};

const generateFullAgreementContent = (agreementData: AgreementData, sellerNames: string) => {
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "Commitment to Purchase Agreement",
          bold: true,
          size: 24
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `This Purchase Agreement made and entered into ${agreementData.date ? agreementData.date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "________________"},`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `The property address is: ${agreementData.propertyAddress || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `2. PURCHASER agrees to buy the entire remaining balance of $${agreementData.balance || "________________"} and shall pay to SELLER $${agreementData.brokerOfferToSeller || "________________"} for the full purchase of said ${agreementData.paperType || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "3. COSTS: PURCHASER will pay title and appraisal costs",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "4. SELLER warrants that all real property taxes that are due and payable have been paid.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "5. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "6. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `7. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "8. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),
    
    // Signature lines
    new Paragraph({
      children: [
        new TextRun({
          text: "______________________________________                    ______________________________________",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${agreementData.primarySellerName || "Primary Seller"}                                        Contract Funding Group, LLC`,
          size: 20
        })
      ],
      spacing: { after: 200 }
    }),
    ...(agreementData.secondarySellerName ? [
      new Paragraph({
        children: [
          new TextRun({
            text: "______________________________________",
            size: 20
          })
        ],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: agreementData.secondarySellerName,
            size: 20
          })
        ],
        spacing: { after: 200 }
      })
    ] : [])
  ];
};